import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchPuzzles } from '../../api/puzzlesApi';

export interface Puzzle {
  id: string;
  title: string;
  description: string;
  difficulty: number;
}

export interface PuzzlesState {
  puzzles: Puzzle[];
  loading: boolean;
  error: string | null;
}

const initialState: PuzzlesState = {
  puzzles: [
    { id: '1', title: 'Crossword #1', description: 'A simple crossword puzzle for beginners.', difficulty: 1 },
    { id: '2', title: 'Sudoku Challenge', description: 'Hard difficulty sudoku.', difficulty: 3 },
  ],
  loading: false,
  error: null,
};

const puzzlesSlice = createSlice({
  name: 'puzzles',
  initialState,
  reducers: {
    fetchPuzzlesStart(state) {
      state.loading = true;
      state.error = null;
    },
    fetchPuzzlesSuccess(state, action: PayloadAction<Puzzle[]>) {
      state.loading = false;
      state.puzzles = action.payload;
    },
    fetchPuzzlesFailure(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },
    addPuzzle(state, action: PayloadAction<Puzzle>) {
      state.puzzles.push(action.payload);
    },
    updatePuzzle(state, action: PayloadAction<Puzzle>) {
      const index = state.puzzles.findIndex(puzzle => puzzle.id === action.payload.id);
      if (index !== -1) {
        state.puzzles[index] = action.payload;
      }
    },
    deletePuzzle(state, action: PayloadAction<string>) {
      state.puzzles = state.puzzles.filter(puzzle => puzzle.id !== action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPuzzles.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchPuzzles.fulfilled, (state, action: any) => {
        state.loading = false;
        // Merge or replace? For now, let's just append if not exists or replace.
        // Actually, since the API is fake/broken, let's NOT overwrite our mock data with empty/error data if we can help it.
        // But for correctness:
        state.puzzles = action.payload;
      })
      .addCase(fetchPuzzles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch';
        // Keep the mock data visible even on error for this demo
      });
  }
});

export const {
  fetchPuzzlesStart,
  fetchPuzzlesSuccess,
  fetchPuzzlesFailure,
  addPuzzle,
  updatePuzzle,
  deletePuzzle,
} = puzzlesSlice.actions;

export default puzzlesSlice.reducer;